import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../context/AlertContext';
import Layout from '../components/Layout';
import API_BASE_URL from '../config/api';
import { Country, State, City } from 'country-state-city';
import {
    CheckCircle, User, Mail, Lock, ChevronRight, ChevronLeft,
    Hash, AlertCircle, Loader2, GitBranch, MapPin,
    Eye, EyeOff, Calendar, Shield, CreditCard, Landmark
} from 'lucide-react';

const Register = () => {
    const { showAlert } = useAlert();
    const navigate = useNavigate();

    // Step Management
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [registrationSuccess, setRegistrationSuccess] = useState(null);
    const [showDeclarationModal, setShowDeclarationModal] = useState(false);

    // Sponsor State
    const [sponsorLoading, setSponsorLoading] = useState(false);
    const [sponsorMessage, setSponsorMessage] = useState({ text: '', type: '' });
    const [sponsorIsFull, setSponsorIsFull] = useState(false);
    const [extremePositions, setExtremePositions] = useState(null);

    // Validation Messages
    const [mobileMessage, setMobileMessage] = useState({ text: '', type: '' });
    const [panMessage, setPanMessage] = useState({ text: '', type: '' });
    const [bankAccountMessage, setBankAccountMessage] = useState({ text: '', type: '' });

    // Password Visibility
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Location States
    const [countries] = useState(Country.getAllCountries());
    const [states, setStates] = useState(State.getStatesOfCountry('IN'));
    const [cities, setCities] = useState([]);
    const [selectedCountryCode, setSelectedCountryCode] = useState('IN');
    const [selectedStateCode, setSelectedStateCode] = useState('');

    // Validation Patterns
    const PATTERNS = {
        mobile: /^[6-9][0-9]{9}$/
    };

    // Form Data
    const [formData, setFormData] = useState({
        // Step 1: Sponsor Details
        sponsorId: '',
        sponsorName: '',
        placement: 'left',

        // Step 2: Personal Details
        fullName: '',
        gender: 'male',
        maritalStatus: 'single',
        nomineeName: '',
        nomineeRelation: '',
        email: '',
        mobile: '',
        dateOfBirth: '',
        panNumber: '',
        bankAccountNumber: '',

        // Step 3: Address Details
        country: 'India',
        state: '',
        city: '',
        pinCode: '',
        fullAddress: '',

        // Step 4: Account Security (KYC removed - user will complete from Profile after login)
        password: '',
        confirmPassword: '',
        transactionPassword: '',

        // Declaration
        agreeTerms: false
    });

    // Steps Configuration (KYC step removed - user will complete from Profile after login)
    const steps = [
        { number: 1, title: 'Sponsor', icon: GitBranch },
        { number: 2, title: 'Personal', icon: User },
        { number: 3, title: 'Address', icon: MapPin },
        { number: 4, title: 'Security', icon: Shield }
    ];

    // Handle Form Changes
    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        let finalValue = type === 'checkbox' ? checked : type === 'file' ? files[0] : value;

        // Sponsor ID - verify on blur handled in JSX
        if (name === 'sponsorId') {
            // Keep it manual as per user request
        }

        // Mobile - numbers only, max 10
        if (name === 'mobile') {
            finalValue = value.replace(/\D/g, '').slice(0, 10);
            if (mobileMessage.text) setMobileMessage({ text: '', type: '' });
        }

        // Pin code - numbers only, max 6
        if (name === 'pinCode') {
            finalValue = value.replace(/\D/g, '').slice(0, 6);
        }

        // Transaction Password - numbers only, max 4
        if (name === 'transactionPassword') {
            finalValue = value.replace(/\D/g, '').slice(0, 4);
        }

        // PAN Number - uppercase, max 10 alphanumeric
        if (name === 'panNumber') {
            finalValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
        }

        // Bank Account Number - digits only, max 18
        if (name === 'bankAccountNumber') {
            finalValue = value.replace(/\D/g, '').slice(0, 18);
        }


        // Full Name - All caps
        if (name === 'fullName') {
            finalValue = value.toUpperCase();
        }

        setFormData(prev => ({ ...prev, [name]: finalValue }));
    };

    // Verify Sponsor and Get Positions
    const verifySponsor = async (sponsorCode) => {
        if (!sponsorCode) {
            setSponsorMessage({ text: '', type: '' });
            setSponsorIsFull(false);
            setExtremePositions(null);
            setFormData(prev => ({ ...prev, sponsorName: '' }));
            return;
        }

        setSponsorLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/user/check_sponsor_positions.php?sponsor_id=${sponsorCode}`);
            const data = await response.json();

            if (data.success) {
                setFormData(prev => ({ ...prev, sponsorName: data?.sponsor?.name || '' }));

                // Store branch details for smarter UI
                const branchInfo = {
                    left: data?.left_branch,
                    right: data?.right_branch
                };
                setExtremePositions(branchInfo);

                // For direct availability
                setSponsorIsFull(!!data?.is_full);

                if (data.is_full) {
                    setSponsorMessage({
                        text: `⚠️ Sponsor is FULL! You must choose an Extreme position.`,
                        type: 'warning'
                    });
                    setFormData(prev => ({ ...prev, placement: 'extreme_left' }));
                } else {
                    // Set default to first available
                    if (data?.available_positions && data.available_positions.length > 0) {
                        setFormData(prev => ({ ...prev, placement: data.available_positions[0] }));
                    }

                    if (data?.left_branch?.is_available && data?.right_branch?.is_available) {
                        setSponsorMessage({
                            text: `✓ Sponsor: ${data?.sponsor?.name || 'User'}. Both branches available.`,
                            type: 'success'
                        });
                    } else {
                        const avail = data?.left_branch?.is_available ? 'LEFT' : 'RIGHT';
                        setSponsorMessage({
                            text: `✓ Sponsor: ${data?.sponsor?.name || 'User'}. Direct ${avail} available. Opposite is FULL.`,
                            type: 'success'
                        });
                    }
                }
            } else {
                setFormData(prev => ({ ...prev, sponsorName: '' }));
                setSponsorIsFull(false);
                setExtremePositions(null);
                setSponsorMessage({
                    text: '❌ Invalid Sponsor ID. ' + (data.message || 'Sponsor not found.'),
                    type: 'error'
                });
            }
        } catch (e) {
            console.error("Sponsor check failed", e);
            setSponsorMessage({
                text: '❌ Failed to verify sponsor.',
                type: 'error'
            });
        } finally {
            setSponsorLoading(false);
        }
    };

    // Handle Country Change
    const handleCountryChange = (e) => {
        const countryCode = e.target.value;
        const countryName = e.target.options[e.target.selectedIndex].text;
        setSelectedCountryCode(countryCode);
        setFormData(prev => ({ ...prev, country: countryName, state: '', city: '' }));
        const countryStates = State.getStatesOfCountry(countryCode);
        setStates(countryStates);
        setCities([]);
        setSelectedStateCode('');
    };

    // Handle State Change
    const handleStateChange = (e) => {
        const stateCode = e.target.value;
        const stateName = e.target.options[e.target.selectedIndex].text;
        setSelectedStateCode(stateCode);
        setFormData(prev => ({ ...prev, state: stateName, city: '' }));
        const stateCities = City.getCitiesOfState(selectedCountryCode, stateCode);
        setCities(stateCities);
    };

    // Mobile Validation
    const handleMobileBlur = () => {
        if (!formData.mobile) return;
        if (!PATTERNS.mobile.test(formData.mobile)) {
            setMobileMessage({ text: '⚠️ Invalid mobile. Must be 10 digits starting with 6-9.', type: 'error' });
        } else {
            setMobileMessage({ text: '✓ Valid mobile number', type: 'success' });
        }
    };

    // PAN Duplicate Check
    const handlePanBlur = async () => {
        if (!formData.panNumber) { setPanMessage({ text: '', type: '' }); return; }
        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
        if (!panRegex.test(formData.panNumber)) {
            setPanMessage({ text: '⚠️ Invalid PAN format (e.g. ABCDE1234F)', type: 'error' });
            return;
        }
        try {
            const response = await fetch(`${API_BASE_URL}/auth/check_kyc.php?type=pan&value=${formData.panNumber}`);
            const data = await response.json();
            if (data.exists) {
                setPanMessage({ text: '❌ ' + (data.message || 'PAN already used'), type: 'error' });
            } else {
                setPanMessage({ text: '✓ PAN number is available', type: 'success' });
            }
        } catch (e) {
            console.error('PAN check failed', e);
            setPanMessage({ text: '', type: '' });
        }
    };

    // Bank Account Duplicate Check
    const handleBankAccountBlur = async () => {
        if (!formData.bankAccountNumber) { setBankAccountMessage({ text: '', type: '' }); return; }
        if (formData.bankAccountNumber.length < 9) {
            setBankAccountMessage({ text: '⚠️ Account number must be at least 9 digits', type: 'error' });
            return;
        }
        try {
            const response = await fetch(`${API_BASE_URL}/auth/check_kyc.php?type=bank&value=${formData.bankAccountNumber}`);
            const data = await response.json();
            if (data.exists) {
                setBankAccountMessage({ text: '❌ ' + (data.message || 'Bank account already used'), type: 'error' });
            } else {
                setBankAccountMessage({ text: '✓ Bank account number is available', type: 'success' });
            }
        } catch (e) {
            console.error('Bank check failed', e);
            setBankAccountMessage({ text: '', type: '' });
        }
    };

    // KYC validation functions removed - KYC is done from Profile page after login

    // Step Validation
    const validateStep = () => {
        setError('');

        if (currentStep === 1) {
            if (!formData.sponsorId) {
                setError('Please enter a Sponsor ID');
                return false;
            }
            if (sponsorMessage.type === 'error') {
                setError('Please provide a valid Sponsor ID');
                return false;
            }
            if (sponsorIsFull && !extremePositions) {
                setError('Sponsor is full. Please choose a different sponsor.');
                return false;
            }
        }

        if (currentStep === 2) {
            if (!formData.fullName || !formData.email || !formData.mobile || !formData.dateOfBirth) {
                setError('Please fill in all required fields');
                return false;
            }
            if (formData.mobile.length !== 10) {
                setError('Mobile number must be 10 digits');
                return false;
            }
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                setError('Please enter a valid email address');
                return false;
            }
            // PAN validation
            if (!formData.panNumber || formData.panNumber.length !== 10) {
                setError('Please enter a valid 10-character PAN Card Number');
                return false;
            }
            const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
            if (!panRegex.test(formData.panNumber)) {
                setError('PAN Card format is invalid (e.g. ABCDE1234F)');
                return false;
            }
            // Bank account validation
            if (!formData.bankAccountNumber || formData.bankAccountNumber.length < 9) {
                setError('Please enter a valid Bank Account Number (min 9 digits)');
                return false;
            }
            // Check if PAN or bank account showed duplicate
            if (panMessage.type === 'error') {
                setError('PAN Card Number issue: ' + panMessage.text);
                return false;
            }
            if (bankAccountMessage.type === 'error') {
                setError('Bank Account issue: ' + bankAccountMessage.text);
                return false;
            }
            // Age validation (18+)
            const dob = new Date(formData.dateOfBirth);
            const today = new Date();
            let age = today.getFullYear() - dob.getFullYear();
            const monthDiff = today.getMonth() - dob.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
            if (age < 18) {
                showAlert('You must be at least 18 years old to register', 'error');
                setError('You must be at least 18 years old to register');
                return false;
            }
        }

        if (currentStep === 3) {
            if (!formData.state || !formData.city || !formData.pinCode) {
                setError('Please fill in all required address fields');
                return false;
            }
            if (formData.pinCode.length !== 6) {
                setError('Pin code must be 6 digits');
                return false;
            }
        }

        // KYC step removed - validation moved to Profile page after login

        if (currentStep === 4) {
            if (!formData.password || !formData.confirmPassword || !formData.transactionPassword) {
                setError('Please fill in all security fields');
                return false;
            }
            if (formData.password.length < 6) {
                setError('Password must be at least 6 characters');
                return false;
            }
            if (formData.password !== formData.confirmPassword) {
                setError('Passwords do not match');
                return false;
            }
            if (formData.transactionPassword && formData.transactionPassword.length !== 4) {
                setError('Transaction Password must be exactly 4 digits');
                return false;
            }
            if (!formData.agreeTerms) {
                setError('Please accept the Terms & Conditions');
                return false;
            }
        }

        return true;
    };

    const handleNext = () => {
        if (validateStep()) {
            setCurrentStep(prev => prev + 1);
        }
    };

    const handlePrevious = () => {
        setError('');
        setCurrentStep(prev => prev - 1);
    };

    // Submit Registration
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateStep()) return;

        setLoading(true);
        setError('');

        try {
            const formDataToSend = new FormData();

            // Map form fields to backend expected names
            formDataToSend.append('fullName', formData.fullName);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('mobile', formData.mobile);
            formDataToSend.append('password', formData.password);
            formDataToSend.append('dateOfBirth', formData.dateOfBirth);
            formDataToSend.append('sponsorId', formData.sponsorId);
            formDataToSend.append('placement', formData.placement);

            // Address
            formDataToSend.append('country', formData.country);
            formDataToSend.append('state', formData.state);
            formDataToSend.append('city', formData.city);
            formDataToSend.append('pinCode', formData.pinCode);
            formDataToSend.append('fullAddress', formData.fullAddress);

            // PAN & Bank Account
            formDataToSend.append('panNumber', formData.panNumber);
            formDataToSend.append('accountNumber', formData.bankAccountNumber);

            // KYC removed - user will complete from Profile after login

            // Security
            if (formData.transactionPassword) {
                formDataToSend.append('transactionPassword', formData.transactionPassword);
            }

            console.log('Submitting registration:', Object.fromEntries(formDataToSend.entries()));

            const response = await fetch(`${API_BASE_URL}/auth/register.php`, {
                method: 'POST',
                body: formDataToSend
            });

            const data = await response.json();

            if (data.success) {
                // Determine display name (username is usually user_id in this system, but let's be safe)
                const displayData = {
                    ...data.data,
                    // If backend doesn't return full_name, use what we sent
                    full_name: data.data.full_name || formData.fullName
                };
                setRegistrationSuccess(displayData);
                // navigate('/dashboard'); // Removed immediate navigation
            } else {
                setError(data.message || 'Registration failed. Please try again.');
            }
        } catch (err) {
            console.error('Registration error:', err);
            setError('Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const passwordStrength = formData.password.length >= 8 ? 'strong' : formData.password.length >= 6 ? 'medium' : 'weak';

    return (
        <Layout>
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Register New Member</h1>
                    <p className="text-gray-500 mt-1 text-sm">Add a new member to your downline network</p>
                </div>

                {/* Stepper */}
                <div className="mb-6 overflow-x-auto pb-2">
                    <div className="flex items-center justify-between min-w-[500px]">
                        {steps.map((step, index) => {
                            const StepIcon = step.icon;
                            const isActive = currentStep === step.number;
                            const isCompleted = currentStep > step.number;

                            return (
                                <React.Fragment key={step.number}>
                                    <div className="flex flex-col items-center flex-1">
                                        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all ${isCompleted ? 'bg-green-500 text-white' :
                                            isActive ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg' :
                                                'bg-gray-200 text-gray-400'
                                            }`}>
                                            {isCompleted ? <CheckCircle size={20} /> : <StepIcon size={18} />}
                                        </div>
                                        <p className={`mt-1.5 text-xs font-medium ${isActive ? 'text-orange-600' : isCompleted ? 'text-green-600' : 'text-gray-400'
                                            }`}>{step.title}</p>
                                    </div>
                                    {index < steps.length - 1 && (
                                        <div className={`flex-1 h-1 mx-2 rounded ${currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'}`} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
                        <AlertCircle size={18} />
                        {error}
                    </div>
                )}

                {/* Form Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
                    <form onSubmit={handleSubmit}>

                        {/* STEP 1: Sponsor Details */}
                        {currentStep === 1 && (
                            <div className="space-y-5">
                                <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
                                    <div className="p-2 bg-blue-100 rounded-lg text-blue-600"><GitBranch size={20} /></div>
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">Sponsor & Branch Selection</h2>
                                        <p className="text-xs text-gray-500">Enter sponsor details and choose placement</p>
                                    </div>
                                </div>

                                {/* Sponsor ID */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                        Sponsor User ID <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                        <input
                                            type="text"
                                            name="sponsorId"
                                            value={formData.sponsorId}
                                            onChange={(e) => {
                                                handleChange(e);
                                                // Auto-verify on type (debounced)
                                            }}
                                            onBlur={() => verifySponsor(formData.sponsorId)}
                                            className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 uppercase"
                                            placeholder="Enter Sponsor User ID (e.g. NX123456)"
                                        />
                                        {sponsorLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 text-orange-500 animate-spin" size={18} />}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">Leave empty or change to register under a different sponsor</p>
                                </div>

                                {/* Sponsor Name */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Sponsor Name</label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                        <input
                                            type="text"
                                            readOnly
                                            value={formData.sponsorName}
                                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 text-gray-600 cursor-not-allowed"
                                            placeholder="Auto-filled from Sponsor ID"
                                        />
                                    </div>
                                </div>

                                {/* Sponsor Message */}
                                {sponsorMessage.text && (
                                    <div className={`rounded-xl p-3 text-sm ${sponsorMessage.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' :
                                        sponsorMessage.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700' :
                                            'bg-yellow-50 border border-yellow-200 text-yellow-700'
                                        }`}>
                                        {sponsorMessage.text}
                                    </div>
                                )}

                                {/* Branch Selection */}
                                {extremePositions && extremePositions.left && extremePositions.right && (
                                    <div className="space-y-4 animate-fadeIn">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Placement Position <span className="text-red-500">*</span>
                                        </label>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {/* LEFT SIDE */}
                                            {extremePositions.left.is_available ? (
                                                <label className={`cursor-pointer rounded-xl border-2 p-3 flex items-center justify-center gap-2 transition-all ${formData.placement === 'left' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600'}`}>
                                                    <input type="radio" name="placement" value="left" checked={formData.placement === 'left'} onChange={handleChange} className="hidden" />
                                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${formData.placement === 'left' ? 'border-blue-600' : 'border-gray-400'}`}>
                                                        {formData.placement === 'left' && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                                                    </div>
                                                    <span className="font-semibold text-sm uppercase">⬅️ Left</span>
                                                </label>
                                            ) : (
                                                <label className={`cursor-pointer rounded-xl border-2 p-3 transition-all ${formData.placement === 'extreme_left' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600'}`}>
                                                    <input type="radio" name="placement" value="extreme_left" checked={formData.placement === 'extreme_left'} onChange={handleChange} className="hidden" />
                                                    <div className="text-center">
                                                        <p className="font-bold text-sm uppercase">🌳 Extreme Left</p>
                                                        <p className="text-[10px] opacity-70">Under: {extremePositions.left.extreme_target?.name || '...'}</p>
                                                    </div>
                                                </label>
                                            )}

                                            {/* RIGHT SIDE */}
                                            {extremePositions.right.is_available ? (
                                                <label className={`cursor-pointer rounded-xl border-2 p-3 flex items-center justify-center gap-2 transition-all ${formData.placement === 'right' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-600'}`}>
                                                    <input type="radio" name="placement" value="right" checked={formData.placement === 'right'} onChange={handleChange} className="hidden" />
                                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${formData.placement === 'right' ? 'border-purple-600' : 'border-gray-400'}`}>
                                                        {formData.placement === 'right' && <div className="w-2 h-2 rounded-full bg-purple-600" />}
                                                    </div>
                                                    <span className="font-semibold text-sm uppercase">➡️ Right</span>
                                                </label>
                                            ) : (
                                                <label className={`cursor-pointer rounded-xl border-2 p-3 transition-all ${formData.placement === 'extreme_right' ? 'border-fuchsia-500 bg-fuchsia-50 text-fuchsia-700' : 'border-gray-200 text-gray-600'}`}>
                                                    <input type="radio" name="placement" value="extreme_right" checked={formData.placement === 'extreme_right'} onChange={handleChange} className="hidden" />
                                                    <div className="text-center">
                                                        <p className="font-bold text-sm uppercase">🌳 Extreme Right</p>
                                                        <p className="text-[10px] opacity-70">Under: {extremePositions.right.extreme_target?.name || '...'}</p>
                                                    </div>
                                                </label>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* STEP 2: Personal Details */}
                        {currentStep === 2 && (
                            <div className="space-y-5">
                                <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
                                    <div className="p-2 bg-green-100 rounded-lg text-green-600"><User size={20} /></div>
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">Personal Information</h2>
                                        <p className="text-xs text-gray-500">Basic details of the new member</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name <span className="text-red-500">*</span></label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                            <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="As per ID proof" required />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email <span className="text-red-500">*</span></label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                            <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="email@example.com" required />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Mobile Number <span className="text-red-500">*</span></label>
                                        <div className="relative flex">
                                            <div className="bg-gray-100 border border-gray-200 border-r-0 rounded-l-xl px-3 py-2.5 text-gray-600 font-medium text-sm">+91</div>
                                            <input type="tel" name="mobile" value={formData.mobile} onChange={handleChange} onBlur={handleMobileBlur} className="w-full pl-3 pr-4 py-2.5 border border-gray-200 rounded-r-xl focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="9876543210" maxLength="10" required />
                                        </div>
                                        {mobileMessage.text && <p className={`text-xs mt-1 ${mobileMessage.type === 'error' ? 'text-red-500' : 'text-green-600'}`}>{mobileMessage.text}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Date of Birth <span className="text-red-500">*</span></label>
                                        <div className="relative">
                                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                            <input
                                                type="date"
                                                name="dateOfBirth"
                                                value={formData.dateOfBirth}
                                                onChange={handleChange}
                                                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                                                min="1920-01-01"
                                                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                required
                                            />
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">Must be 18+ years old</p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Gender <span className="text-red-500">*</span></label>
                                        <select
                                            name="gender"
                                            value={formData.gender}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                            required
                                        >
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Marital Status <span className="text-red-500">*</span></label>
                                        <select
                                            name="maritalStatus"
                                            value={formData.maritalStatus}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                            required
                                        >
                                            <option value="single">Single</option>
                                            <option value="married">Married</option>
                                            <option value="divorced">Divorced</option>
                                            <option value="widowed">Widowed</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nominee Name <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            name="nomineeName"
                                            value={formData.nomineeName}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                            placeholder="Enter Nominee Name"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nominee Relation <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            name="nomineeRelation"
                                            value={formData.nomineeRelation}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                            placeholder="e.g. Spouse, Father, Mother"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">PAN Card Number <span className="text-red-500">*</span></label>
                                        <div className="relative">
                                            <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                            <input
                                                type="text"
                                                name="panNumber"
                                                value={formData.panNumber}
                                                onChange={handleChange}
                                                onBlur={handlePanBlur}
                                                className={`w-full pl-10 pr-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 uppercase font-mono tracking-wider ${panMessage.type === 'error' ? 'border-red-400 bg-red-50/30' : panMessage.type === 'success' ? 'border-green-400 bg-green-50/30' : 'border-gray-200'}`}
                                                placeholder="ABCDE1234F"
                                                maxLength="10"
                                                required
                                            />
                                        </div>
                                        {panMessage.text ? <p className={`text-xs mt-1 font-medium ${panMessage.type === 'error' ? 'text-red-500' : 'text-green-600'}`}>{panMessage.text}</p> : <p className="text-xs text-gray-500 mt-1">10-character alphanumeric PAN</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Bank Account Number <span className="text-red-500">*</span></label>
                                        <div className="relative">
                                            <Landmark className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                            <input
                                                type="text"
                                                name="bankAccountNumber"
                                                value={formData.bankAccountNumber}
                                                onChange={handleChange}
                                                onBlur={handleBankAccountBlur}
                                                className={`w-full pl-10 pr-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono tracking-wider ${bankAccountMessage.type === 'error' ? 'border-red-400 bg-red-50/30' : bankAccountMessage.type === 'success' ? 'border-green-400 bg-green-50/30' : 'border-gray-200'}`}
                                                placeholder="Enter Account Number"
                                                maxLength="18"
                                                required
                                            />
                                        </div>
                                        {bankAccountMessage.text ? <p className={`text-xs mt-1 font-medium ${bankAccountMessage.type === 'error' ? 'text-red-500' : 'text-green-600'}`}>{bankAccountMessage.text}</p> : <p className="text-xs text-gray-500 mt-1">Your bank account number for payouts</p>}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* STEP 3: Address Details */}
                        {currentStep === 3 && (
                            <div className="space-y-5">
                                <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
                                    <div className="p-2 bg-purple-100 rounded-lg text-purple-600"><MapPin size={20} /></div>
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">Address Details</h2>
                                        <p className="text-xs text-gray-500">Residential address for communication</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Country</label>
                                        <select value={selectedCountryCode} onChange={handleCountryChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white">
                                            {countries.map(c => <option key={c.isoCode} value={c.isoCode}>{c.name}</option>)}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">State <span className="text-red-500">*</span></label>
                                        <select value={selectedStateCode} onChange={handleStateChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white" required>
                                            <option value="">Select State</option>
                                            {states.map(s => <option key={s.isoCode} value={s.isoCode}>{s.name}</option>)}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">City <span className="text-red-500">*</span></label>
                                        <select name="city" value={formData.city} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white" required>
                                            <option value="">Select City</option>
                                            {cities.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Pin Code <span className="text-red-500">*</span></label>
                                        <input type="text" name="pinCode" value={formData.pinCode} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="6-digit pin code" maxLength="6" required />
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Address</label>
                                        <textarea name="fullAddress" value={formData.fullAddress} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="House No, Street, Locality..." rows="2" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* KYC Step Removed - Users will complete KYC from Profile page after login */}

                        {/* STEP 4: Account Security */}
                        {currentStep === 4 && (
                            <div className="space-y-5">
                                <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
                                    <div className="p-2 bg-red-100 rounded-lg text-red-600"><Shield size={20} /></div>
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">Account Security</h2>
                                        <p className="text-xs text-gray-500">Create secure login credentials</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Password <span className="text-red-500">*</span></label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                            <input type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleChange} className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="Min 6 characters" minLength="6" required />
                                            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                        {formData.password && (
                                            <div className="mt-1.5">
                                                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                                    <div className={`h-full transition-all ${passwordStrength === 'strong' ? 'bg-green-500 w-full' : passwordStrength === 'medium' ? 'bg-yellow-500 w-2/3' : 'bg-red-500 w-1/3'}`} />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Confirm Password <span className="text-red-500">*</span></label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                            <input type={showConfirmPassword ? "text" : "password"} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="Confirm password" required />
                                            <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                                                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                            {formData.confirmPassword && formData.password === formData.confirmPassword && <CheckCircle className="absolute right-10 top-1/2 -translate-y-1/2 text-green-500" size={18} />}
                                        </div>
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Transaction PIN (4-Digit) <span className="text-red-500">*</span></label>
                                        <div className="relative max-w-xs">
                                            <Shield className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                            <input type="password" name="transactionPassword" value={formData.transactionPassword} onChange={handleChange} className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-lg tracking-[0.3em]" placeholder="XXXX" maxLength="4" required />
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">Required for all wallet transactions and withdrawals</p>
                                    </div>
                                </div>

                                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
                                    <h3 className="font-semibold text-blue-900 mb-2">📋 Review Details</h3>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <p><span className="font-medium text-gray-600">Sponsor:</span> {formData.sponsorName || '-'}</p>
                                        <p><span className="font-medium text-gray-600">Branch:</span> {formData.placement?.toUpperCase()}</p>
                                        <p><span className="font-medium text-gray-600">Name:</span> {formData.fullName || '-'}</p>
                                        <p><span className="font-medium text-gray-600">Email:</span> {formData.email || '-'}</p>
                                        <p><span className="font-medium text-gray-600">Mobile:</span> +91 {formData.mobile || '-'}</p>
                                        <p><span className="font-medium text-gray-600">City:</span> {formData.city || '-'}</p>
                                        <p><span className="font-medium text-gray-600">PAN:</span> {formData.panNumber || '-'}</p>
                                        <p><span className="font-medium text-gray-600">Bank A/C:</span> {formData.bankAccountNumber ? '****' + formData.bankAccountNumber.slice(-4) : '-'}</p>
                                    </div>
                                </div>

                                {/* Declaration */}
                                <div className="bg-gray-50 rounded-xl p-4 border-2 border-dashed border-gray-200">
                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <input type="checkbox" name="agreeTerms" checked={formData.agreeTerms} onChange={(e) => { if (!formData.agreeTerms) { e.preventDefault(); setShowDeclarationModal(true); } else { setFormData(prev => ({ ...prev, agreeTerms: false })); } }} className="w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500 mt-0.5" />
                                        <span className="text-sm text-gray-600">
                                            <span className="font-bold text-gray-900">Declaration:</span> I have read and agree to the{' '}
                                            <button type="button" onClick={() => setShowDeclarationModal(true)} className="text-orange-600 font-bold hover:underline">Direct Seller Agreement</button>{' '}
                                            as per Gazette of India. I declare that the details furnished above are true and correct.
                                        </span>
                                    </label>
                                </div>
                            </div>
                        )}

                        {/* Navigation Buttons */}
                        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={handlePrevious}
                                disabled={currentStep === 1}
                                className="flex items-center gap-1.5 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                                <ChevronLeft size={18} /> Previous
                            </button>

                            {currentStep < 4 ? (
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    className="flex items-center gap-1.5 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all text-sm"
                                >
                                    Next <ChevronRight size={18} />
                                </button>
                            ) : (
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex items-center gap-1.5 px-5 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 text-sm"
                                >
                                    {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</> : <><CheckCircle size={18} /> Complete Registration</>}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div >

            {/* Registration Success Modal */}
            {registrationSuccess && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md animate-fadeIn" />
                    <div className="relative bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl border border-gray-100 animate-scaleIn text-center space-y-6">
                        <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-2">
                            <CheckCircle size={48} />
                        </div>

                        <div className="space-y-2">
                            <h2 className="text-2xl font-black text-gray-900">Registration Successful!</h2>
                            <p className="text-gray-500 font-medium">New member account has been created.</p>
                        </div>

                        <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 space-y-4">
                            <div className="flex justify-between items-center py-2 border-b border-slate-200/50">
                                <span className="text-gray-400 text-xs font-bold uppercase tracking-wider">User ID</span>
                                <span className="text-blue-600 font-black text-lg font-mono">{registrationSuccess.user_id}</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-slate-200/50">
                                <span className="text-gray-400 text-xs font-bold uppercase tracking-wider">Full Name</span>
                                <span className="text-gray-800 font-bold">{registrationSuccess.full_name}</span>
                            </div>
                            <div className="flex justify-between items-center py-2">
                                <span className="text-gray-400 text-xs font-bold uppercase tracking-wider">Email</span>
                                <span className="text-gray-600 font-semibold text-sm">{registrationSuccess.email}</span>
                            </div>
                        </div>

                        <div className="p-4 bg-amber-50 rounded-xl border border-amber-100 flex gap-3 text-left">
                            <AlertCircle className="text-amber-500 shrink-0" size={20} />
                            <p className="text-[11px] text-amber-700 font-bold leading-relaxed">
                                Please share these details with the new member. They will need the User ID to login.
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <button
                                onClick={() => {
                                    setRegistrationSuccess(null);
                                    window.location.reload(); // Reset form for next registration or just clear state
                                }}
                                className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-all text-sm"
                            >
                                Register Another
                            </button>
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-bold hover:shadow-lg hover:shadow-blue-600/20 transition-all text-sm flex items-center justify-center gap-2"
                            >
                                Go to Dashboard <ChevronRight size={18} />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Direct Seller Agreement Modal */}
            {showDeclarationModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setShowDeclarationModal(false)} />
                    <div className="relative bg-white rounded-2xl w-full max-w-2xl shadow-2xl border border-gray-100 max-h-[85vh] flex flex-col">
                        <div className="flex items-center justify-between p-5 border-b border-gray-100">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Direct Seller Agreement</h3>
                                <p className="text-xs text-gray-500 mt-0.5">As per Gazette of India</p>
                            </div>
                            <button onClick={() => setShowDeclarationModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors p-1">✕</button>
                        </div>
                        <div className="overflow-y-auto p-6 space-y-5 text-sm text-gray-700 leading-relaxed flex-1">
                            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
                                <p className="text-xs text-orange-600 font-bold uppercase tracking-wider mb-1">Compliance with Consumer Protection (Direct Selling) Rules, 2021</p>
                                <p className="text-xs text-orange-500">(As notified in the Gazette of India)</p>
                                <p className="text-base font-black text-gray-900 mt-2">SWIFT2AI LIFECARE PRIVATE LIMITED</p>
                            </div>
                            <p>In accordance with the Consumer Protection Act, 2019 and the Consumer Protection (Direct Selling) Rules, 2021, as notified by the Government of India in the Gazette of India, <strong>SWIFT2AI LIFECARE PRIVATE LIMITED</strong> ("Company") affirms and declares its compliance with the statutory requirements governing direct selling entities in India.</p>

                            <div className="space-y-4">
                                <div><h4 className="font-bold text-gray-900 mb-1">1. Legal Status of Direct Selling Entity</h4><p>The Company is a body corporate incorporated under the Companies Act, 2013 and is engaged in direct selling of products in India in accordance with applicable laws.</p></div>

                                <div><h4 className="font-bold text-gray-900 mb-1">2. Prohibition of Pyramid and Money Circulation Schemes</h4><p>The Company does not promote, operate, or participate in any pyramid, chain, or money circulation schemes, whether directly or indirectly. The Company's business model is strictly based on the sale of genuine products and services.</p></div>

                                <div><h4 className="font-bold text-gray-900 mb-1">3. Product-Based Compensation</h4><p>Any incentives, commissions, or benefits payable to direct sellers are derived solely from the sale of products and not from recruitment or enrollment of persons.</p></div>

                                <div><h4 className="font-bold text-gray-900 mb-1">4. Mandatory Policies and Disclosures</h4><p>The Company maintains and publishes the following policies in compliance with the Rules:</p>
                                    <ul className="list-disc pl-5 mt-1 space-y-0.5 text-gray-600"><li>Terms and Conditions</li><li>Shipping Policy</li><li>Return and Refund Policy</li><li>Cancellation Policy</li><li>Grievance Redressal Mechanism</li><li>Direct Seller Code of Conduct</li></ul></div>

                                <div><h4 className="font-bold text-gray-900 mb-1">5. Prohibition of Misleading Representations</h4><p>The Company and its direct sellers are prohibited from making any false, misleading, or exaggerated representations relating to:</p>
                                    <ul className="list-disc pl-5 mt-1 space-y-0.5 text-gray-600"><li>Income or earning potential</li><li>Health, therapeutic, or medical benefits</li><li>Product quality or performance beyond substantiated claims</li></ul></div>

                                <div><h4 className="font-bold text-gray-900 mb-1">6. Grievance Redressal Mechanism</h4><p>The Company has established a grievance redressal mechanism in accordance with Rule 4 of the Consumer Protection (Direct Selling) Rules, 2021, for addressing consumer and direct seller complaints in a timely manner.</p></div>

                                <div><h4 className="font-bold text-gray-900 mb-1">7. Consumer Protection and Transparency</h4><p>The Company ensures that consumers are provided with:</p>
                                    <ul className="list-disc pl-5 mt-1 space-y-0.5 text-gray-600"><li>Complete and accurate product information</li><li>Proper invoices and receipts</li><li>Transparent pricing and policies</li><li>Fair and ethical business practices</li></ul></div>

                                <div><h4 className="font-bold text-gray-900 mb-1">8. Direct Seller Obligations</h4><p>All direct sellers associated with the Company are required to comply with:</p>
                                    <ul className="list-disc pl-5 mt-1 space-y-0.5 text-gray-600"><li>The Consumer Protection (Direct Selling) Rules, 2021</li><li>The Company's internal policies and code of conduct</li><li>All applicable central and state laws and regulations</li></ul></div>

                                <div><h4 className="font-bold text-gray-900 mb-1">9. Compliance Declaration</h4><p>The Company affirms that it conducts its direct selling operations in compliance with the Consumer Protection Act, 2019 and the Consumer Protection (Direct Selling) Rules, 2021, as notified in the Gazette of India, and undertakes to adhere to all statutory obligations imposed thereunder.</p></div>
                            </div>
                        </div>
                        <div className="p-5 border-t border-gray-100 flex gap-3">
                            <button
                                onClick={() => setShowDeclarationModal(false)}
                                className="flex-1 py-3 bg-gray-100 text-gray-600 rounded-xl font-bold hover:bg-gray-200 transition-all text-sm"
                            >
                                Close
                            </button>
                            <button
                                onClick={() => {
                                    setFormData(prev => ({ ...prev, agreeTerms: true }));
                                    setShowDeclarationModal(false);
                                }}
                                className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-bold hover:shadow-lg transition-all text-sm flex items-center justify-center gap-2"
                            >
                                <CheckCircle size={16} /> I Understand & Agree
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout >
    );
};

export default Register;
